import { useState } from "react";
import { useT } from "../i18n/hooks.ts";
import type { SubBasinLayer } from "../model/gisLayer.ts";
import type { SubBasin, EvalHidProject } from "../model/types.ts";
import { InfoIcon } from "./InfoIcon.tsx";
import { BulkAssigner } from "./wizard/BulkAssigner.tsx";
import { AssignmentMap } from "./wizard/AssignmentMap.tsx";

/**
 * A sub-basin→something assignment block (outlet point or aquifer):
 * optional selection map (if there's a sub-basins GIS layer) + list with
 * checkbox and value selector to apply in bulk — same block for "surface
 * outlet" and "outlet aquifer", parameterized so it's reused, one at a
 * time, right after each entity's own creation form (Step3Outlets.tsx for
 * outlet points, Step4Topology.tsx for aquifers — see each one's own
 * comment for why they're now organized "create + assign" per entity
 * instead of "create everything, then assign everything" across two
 * separate steps) and in each entity's editor tab (OutletPointsPanel.tsx/
 * AquifersPanel.tsx). Lives directly under ui/ (not wizard/) precisely so
 * neither the wizard nor the editor tabs have to import it from the other
 * — see project_evalhid_code_audit.md.
 */
export function TopologyAssignmentSection<TUnassign extends number | null>({
  title,
  gisLayer,
  project,
  isAssigned,
  options,
  valueLabel,
  buttonText,
  unassignValue,
  onAssign,
  selection: mapSelection,
  onChangeSelection: onChangeMapSelection,
}: {
  title: string;
  gisLayer: SubBasinLayer | null;
  project: EvalHidProject;
  isAssigned: (subBasin: SubBasin) => boolean;
  options: { value: number; label: string }[];
  valueLabel: (subBasin: SubBasin) => string;
  buttonText: string;
  /** Whichever sentinel the caller's own field uses for "nothing assigned" — see BulkAssigner.tsx's own doc comment. */
  unassignValue: TUnassign;
  onAssign: (codes: Set<number>, value: number | TUnassign) => void;
  /**
   * Selection shared with the main map (editor context — see EditBar.tsx):
   * when given, clicking sub-basins there selects them here too, and this
   * section's own small map (only useful when there's no main map to click
   * on, e.g. the new-project wizard) isn't shown.
   */
  selection?: Set<number>;
  onChangeSelection?: (selection: Set<number>) => void;
}) {
  const [localSelection, setLocalSelection] = useState<Set<number>>(new Set());
  const usingMainMap = mapSelection != null && onChangeMapSelection != null;
  const selection = usingMainMap ? mapSelection : localSelection;
  const onChangeSelection = usingMainMap ? onChangeMapSelection : setLocalSelection;
  const t = useT();
  return (
    <>
      {usingMainMap && selection.size > 0 && (
        <div className="edit-bar-selection">
          <div className="edit-bar-selection-header">
            <strong>
              {t(
                selection.size > 1
                  ? "subBasinsPanel.multipleSelected"
                  : "subBasinsPanel.oneSelected",
                { n: selection.size }
              )}
            </strong>
            <div className="edit-bar-selection-actions">
              <button
                className="button-icon"
                title={t("subBasinsPanel.clearSelection")}
                onClick={() => onChangeSelection(new Set())}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      <h4>
        {title}
        {(gisLayer || usingMainMap) && (
          <>
            {" "}
            <InfoIcon
              text={t(
                // With a main map/canvas shared with the editor (EditBar.tsx),
                // the actual selection surface is whichever view is active
                // there (map or card view — App.tsx's own mapView toggle,
                // this component has no visibility into it) — unlike this
                // section's own little AssignmentMap.tsx below, which is
                // always literally a map (only shown when !usingMainMap).
                usingMainMap ? "step2.mapOrCardsAssignHelp" : "step2.mapAssignHelp"
              )}
            />
          </>
        )}
      </h4>
      {gisLayer && !usingMainMap && (
        <AssignmentMap
          gisLayer={gisLayer}
          project={project}
          isAssigned={isAssigned}
          selection={selection}
          onChangeSelection={onChangeSelection}
        />
      )}
      <BulkAssigner
        subBasins={project.subBasins}
        valueLabel={valueLabel}
        options={options}
        buttonText={buttonText}
        selection={selection}
        onChangeSelection={onChangeSelection}
        unassignValue={unassignValue}
        onAssign={onAssign}
      />
    </>
  );
}
